import React, { useState, useEffect } from 'react';
import './LimitesModal.css';

function LimitesModal({ isOpen, onClose, data, onSave }) {
  const [formData, setFormData] = useState({
    mise_hors_tension_ht: '',
    mise_hors_tension_bt: '',
    limites_continuites: {
      totalite_realisee: false,
      nvh: false,
      nvx: false,
      custom: []
    },
    limites_acces: {
      locaux_visites: false,
      nvi: false,
      longueurs_non_communiquees: false,
      locaux_non_verifies: false,
      locaux_non_verifies_details: '',
      custom: []
    }
  });

  const miseHorsTensionHT = [
    "Mise hors tension non réalisée**",
    "Mise hors tension partielle (Noté NV dans la suite du rapport)",
    "Mise hors tension totale réalisée",
    "Sans objet, installation Basse Tension"
  ];

  const miseHorsTensionBT = [
    "Mise hors tension non réalisée**",
    "Mise hors tension partielle (Noté NV dans la suite du rapport)",
    "Mise hors tension totale réalisée"
  ];

  useEffect(() => {
    if (isOpen && data) {
      setFormData({
        mise_hors_tension_ht: data.mise_hors_tension_ht || '',
        mise_hors_tension_bt: data.mise_hors_tension_bt || '',
        limites_continuites: {
          totalite_realisee: data.limites_continuites?.totalite_realisee || false,
          nvh: data.limites_continuites?.nvh || false,
          nvx: data.limites_continuites?.nvx || false,
          custom: data.limites_continuites?.custom || []
        },
        limites_acces: {
          locaux_visites: data.limites_acces?.locaux_visites || false,
          nvi: data.limites_acces?.nvi || false,
          longueurs_non_communiquees: data.limites_acces?.longueurs_non_communiquees || false,
          locaux_non_verifies: data.limites_acces?.locaux_non_verifies || false,
          locaux_non_verifies_details: data.limites_acces?.locaux_non_verifies_details || '',
          custom: data.limites_acces?.custom || []
        }
      });
    }
  }, [isOpen, data]);

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateCheckbox = (category, field, checked) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: checked
      }
    }));
  };

  const addCustomItem = (category) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        custom: [...(prev[category].custom || []), { id: Date.now(), text: '', checked: false }]
      }
    }));
  };

  const updateCustomItem = (category, id, field, value) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        custom: prev[category].custom.map(item =>
          item.id === id ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const removeCustomItem = (category, id) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        custom: prev[category].custom.filter(item => item.id !== id)
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Limites d'interventions et conditions de mise hors tension</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        <div className="modal-content">
          <p className="modal-description">
            Définissez ici les limites et conditions qui seront reportées sur la page 6 du rapport PDF.
          </p>

          {/* Conditions de mise hors tension */}
          <div className="modal-section">
            <h3>Conditions de mise hors tension</h3>
            
            <div className="form-group">
              <label>En Haute Tension</label>
              <select
                value={formData.mise_hors_tension_ht}
                onChange={(e) => updateField('mise_hors_tension_ht', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner la condition --</option>
                {miseHorsTensionHT.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>En Basse Tension</label>
              <select
                value={formData.mise_hors_tension_bt}
                onChange={(e) => updateField('mise_hors_tension_bt', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner la condition --</option>
                {miseHorsTensionBT.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Limites continuités */}
          <div className="modal-section">
            <h3>Limites continuités</h3>
            
            <div className="checkbox-list">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_continuites.totalite_realisee}
                  onChange={(e) => updateCheckbox('limites_continuites', 'totalite_realisee', e.target.checked)}
                />
                <span>Les continuités ont été réalisées en totalité</span>
              </label>

              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_continuites.nvh}
                  onChange={(e) => updateCheckbox('limites_continuites', 'nvh', e.target.checked)}
                />
                <span>
                  La vérification de continuité des appareils d'éclairage situés en hauteur &gt;2.5m 
                  inaccessibles, faute de moyen d'accès mis à disposition sont mentionnés « NVH » 
                  dans le rapport. Il conviendra de faire réaliser une mesure de continuité au circuit 
                  de protection avant toute intervention sur ces appareils.
                </span>
              </label>

              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_continuites.nvx}
                  onChange={(e) => updateCheckbox('limites_continuites', 'nvx', e.target.checked)}
                />
                <span>
                  La vérification de continuité des matériels et équipements situés dans des zones 
                  susceptibles de présenter des risques d'explosion sont mentionnés « NVX » dans le rapport.
                </span>
              </label>

              {/* Custom items */}
              {formData.limites_continuites.custom.map(item => (
                <div key={item.id} className="custom-item">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={(e) => updateCustomItem('limites_continuites', item.id, 'checked', e.target.checked)}
                  />
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateCustomItem('limites_continuites', item.id, 'text', e.target.value)}
                    placeholder="Saisir le texte de la limite..."
                    className="form-input"
                  />
                  <button
                    className="btn-remove"
                    onClick={() => removeCustomItem('limites_continuites', item.id)}
                  >
                    🗑️
                  </button>
                </div>
              ))}

              <button
                className="btn-add-custom"
                onClick={() => addCustomItem('limites_continuites')}
              >
                ➕ Ajouter un sélecteur à cocher texte libre si besoin
              </button>
            </div>
          </div>

          {/* Limites accès */}
          <div className="modal-section">
            <h3>Limites accès aux locaux et matériels</h3>
            
            <div className="checkbox-list">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_acces.locaux_visites}
                  onChange={(e) => updateCheckbox('limites_acces', 'locaux_visites', e.target.checked)}
                />
                <span>L'ensemble des locaux accessibles présentés ont été visités.</span>
              </label>

              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_acces.nvi}
                  onChange={(e) => updateCheckbox('limites_acces', 'nvi', e.target.checked)}
                />
                <span>
                  Les appareils sans masse accessibles tels que les cassettes de climatisation, 
                  moteur de volet roulant, éclairages sous vasques, etc… qui n'ont pas pu être 
                  vérifiés pour cause d'inaccessibilité sont mentionnés dans le rapport par l'indication "NVI".
                </span>
              </label>

              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_acces.longueurs_non_communiquees}
                  onChange={(e) => updateCheckbox('limites_acces', 'longueurs_non_communiquees', e.target.checked)}
                />
                <span>
                  Les longueurs des canalisations ne nous ayant pas été communiquées, nous n'avons 
                  pas pu déterminer les courants de court-circuit maximum (Ilmax) et donc nous prononcer 
                  sur l'adaptation du pouvoir de coupure des dispositifs de protection.
                </span>
              </label>

              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.limites_acces.locaux_non_verifies}
                  onChange={(e) => updateCheckbox('limites_acces', 'locaux_non_verifies', e.target.checked)}
                />
                <span>Locaux non vérifiés, faute de clé ou d'accompagnement :</span>
              </label>

              {formData.limites_acces.locaux_non_verifies && (
                <input
                  type="text"
                  value={formData.limites_acces.locaux_non_verifies_details}
                  onChange={(e) => updateCheckbox('limites_acces', 'locaux_non_verifies_details', e.target.value)}
                  placeholder="Préciser les locaux..."
                  className="form-input"
                  style={{ marginLeft: '28px', marginTop: '8px' }}
                />
              )}

              {/* Custom items */}
              {formData.limites_acces.custom.map(item => (
                <div key={item.id} className="custom-item">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={(e) => updateCustomItem('limites_acces', item.id, 'checked', e.target.checked)}
                  />
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateCustomItem('limites_acces', item.id, 'text', e.target.value)}
                    placeholder="Saisir le texte de la limite..."
                    className="form-input"
                  />
                  <button
                    className="btn-remove"
                    onClick={() => removeCustomItem('limites_acces', item.id)}
                  >
                    🗑️
                  </button>
                </div>
              ))}

              <button
                className="btn-add-custom"
                onClick={() => addCustomItem('limites_acces')}
              >
                ➕ Ajouter un sélecteur à cocher texte libre si besoin
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Fermer
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

export default LimitesModal;